'use client'

import {
  captureListReturnState,
  isCandidateJobsListPathname,
  listReturnBackState,
  readListReturnPath,
  readListScrollTop,
  candidateJobDetailPath,
  candidateFromJobBackLabel,
  candidateFromOrganisationBackLabel,
  candidateOrganisationPath,
  navigateWithThreadPreservation,
} from '@yeshuman/sdk'
import { useCallback } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import type { Opportunity } from './use-opportunities'

export type NavigateToCandidateJobDetailOptions = {
  /** When true, job detail shows “Back to saved jobs”. */
  fromShortlist?: boolean
}

/** Job that opened the in-app company page — restored when going back from the company. */
export type CandidateFromJobState = {
  slug: string
  title: string
  fromShortlist?: boolean
  backPath?: string
  listScrollTop?: number
}

export type CandidateFromOrganisationState = {
  slug: string
  name: string
  /** Original inbound job, so company Back still returns to it after opening another role. */
  fromJob?: CandidateFromJobState
}

export type CandidateJobDetailLocationState = {
  fromShortlist?: boolean
  /** List URL including page/filters, set when opening a job from Jobs. */
  backPath?: string
  /** Page-pane scroll offset when the job was opened. */
  listScrollTop?: number
  fromOrganisation?: CandidateFromOrganisationState
}

export type CandidateOrganisationLocationState = {
  fromJob?: CandidateFromJobState
}

function isSafePathSlug(value: unknown): value is string {
  if (typeof value !== 'string') return false
  const slug = value.trim()
  return slug.length > 0 && !slug.includes('/') && !slug.includes('?') && !slug.includes('#')
}

export function readCandidateFromJobState(raw: unknown): CandidateFromJobState | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const record = raw as Record<string, unknown>
  if (!isSafePathSlug(record.slug)) return undefined
  const title = typeof record.title === 'string' ? record.title.trim() : ''
  return {
    slug: record.slug.trim(),
    title: title || 'job',
    ...(record.fromShortlist ? { fromShortlist: true } : {}),
    backPath: readListReturnPath(record, isCandidateJobsListPathname),
    listScrollTop: readListScrollTop(record),
  }
}

export function readCandidateFromOrganisationState(
  raw: unknown,
): CandidateFromOrganisationState | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const record = raw as Record<string, unknown>
  if (!isSafePathSlug(record.slug)) return undefined
  const name = typeof record.name === 'string' ? record.name.trim() : ''
  const fromJob = readCandidateFromJobState(record.fromJob)
  return {
    slug: record.slug.trim(),
    name: name || 'company',
    ...(fromJob ? { fromJob } : {}),
  }
}

export function jobDetailStateFromFromJob(
  fromJob: CandidateFromJobState,
): CandidateJobDetailLocationState {
  return {
    ...(fromJob.fromShortlist ? { fromShortlist: true } : {}),
    ...(fromJob.backPath ? { backPath: fromJob.backPath } : {}),
    ...(fromJob.listScrollTop != null ? { listScrollTop: fromJob.listScrollTop } : {}),
  }
}

export function useCandidateJobDetailFromShortlist(): boolean {
  const location = useLocation()
  return Boolean((location.state as CandidateJobDetailLocationState | null)?.fromShortlist)
}

export function candidateJobsBackTarget(
  fromShortlist: boolean,
  backPath?: string,
  fromOrganisation?: Pick<CandidateFromOrganisationState, 'slug' | 'name'> | null,
): {
  label: string
  to: string
} {
  if (fromOrganisation?.slug?.trim()) {
    return {
      label: candidateFromOrganisationBackLabel(fromOrganisation.name),
      to: candidateOrganisationPath(fromOrganisation.slug),
    }
  }
  const fallback = fromShortlist
    ? { label: 'Back to saved jobs', to: '/candidate/jobs?tab=saved' }
    : { label: 'Back to jobs', to: '/candidate/jobs' }
  if (backPath) return { label: fallback.label, to: backPath }
  return fallback
}

export function candidateOrganisationBackTarget(
  fromJob?: Pick<CandidateFromJobState, 'slug' | 'title'> | null,
): {
  label: string
  to: string
} {
  if (fromJob?.slug?.trim()) {
    return {
      label: candidateFromJobBackLabel(fromJob.title),
      to: candidateJobDetailPath({ public_slug: fromJob.slug, id: fromJob.slug }),
    }
  }
  return { label: 'Back to jobs', to: '/candidate/jobs' }
}

/** Back target that keeps the Jobs list page, filters, and scroll you came from. */
export function useCandidateJobsBackTarget(): {
  label: string
  to: string
  state?: CandidateOrganisationLocationState | { restoreScrollTop: number }
} {
  const location = useLocation()
  const state = (location.state as CandidateJobDetailLocationState | null) ?? {}
  const fromOrganisation = readCandidateFromOrganisationState(state.fromOrganisation)
  if (fromOrganisation) {
    return {
      ...candidateJobsBackTarget(false, undefined, fromOrganisation),
      state: fromOrganisation.fromJob ? { fromJob: fromOrganisation.fromJob } : undefined,
    }
  }
  return {
    ...candidateJobsBackTarget(
      Boolean(state.fromShortlist),
      readListReturnPath(state, isCandidateJobsListPathname),
    ),
    state: listReturnBackState(state),
  }
}

/** Company page Back — to the job you came from, or Jobs. */
export function useCandidateOrganisationBackTarget(): {
  label: string
  to: string
  state?: CandidateJobDetailLocationState
  fromJob?: CandidateFromJobState
} {
  const location = useLocation()
  const fromJob = readCandidateFromJobState(
    (location.state as CandidateOrganisationLocationState | null)?.fromJob,
  )
  const target = candidateOrganisationBackTarget(fromJob)
  if (!fromJob) return target
  return {
    ...target,
    fromJob,
    state: jobDetailStateFromFromJob(fromJob),
  }
}

/** Location state when opening a role from the in-app company page. */
export function openRoleJobLinkState(
  jobSlug: string,
  organisation: Pick<CandidateFromOrganisationState, 'slug' | 'name'>,
  fromJob?: CandidateFromJobState,
): CandidateJobDetailLocationState {
  const listReturn = fromJob ? jobDetailStateFromFromJob(fromJob) : {}
  if (fromJob && jobSlug === fromJob.slug) return listReturn
  return {
    ...listReturn,
    fromOrganisation: {
      slug: organisation.slug,
      name: organisation.name,
      ...(fromJob ? { fromJob } : {}),
    },
  }
}

/** Navigate to `/candidate/jobs/:slug` (or marketplace variant) with thread preservation. */
export function useNavigateToCandidateJobDetail(
  options: NavigateToCandidateJobDetailOptions = {},
) {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const fromShortlist = Boolean(options.fromShortlist)

  return useCallback(
    (opportunity: Opportunity) => {
      const state: CandidateJobDetailLocationState = {
        ...captureListReturnState(location),
        ...(fromShortlist ? { fromShortlist: true } : {}),
      }
      navigateWithThreadPreservation(navigate, candidateJobDetailPath(opportunity), searchParams, {
        state,
      })
    },
    [fromShortlist, location, navigate, searchParams],
  )
}
